import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { TextField, InputAdornment } from "@mui/material";
import Swal from "sweetalert2";
import PlantillaScreen from "../../../helpers/PlantillaScreen";
import { LoaderScreen } from "../../../components/LoaderScreen";
import request from "../../../../Helpers/API";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { FamiliaRefaccionesModal } from "./FamiliaRefaccionesModal";

// Listado del catálogo Familia/Sub Familia por número de Parte. Alta/edición
// con FamiliaRefaccionesModal; el listado solo se recarga al guardar. Detalle
// completo en README.md.
const FamiliaRefaccionesMainScreen = () => {
    const history = useHistory();

    const [listado, setListado] = useState([]);
    const [busqueda, setBusqueda] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [registroEditar, setRegistroEditar] = useState(null);
    // Paginación (mismo patrón que RealizarConteoScreen.js, Auditoría) —
    // ver README para el motivo (listado de miles de filas).
    const [paginaActual, setPaginaActual] = useState(1);
    const [registrosPorPagina, setRegistrosPorPagina] = useState(20);
    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });

    const cargarListado = () => {
        setLoader((prev) => ({ ...prev, mostrar: true }));
        request
            .get("PartesFamilia/Listado")
            .then((result) => {
                setListado(result || []);
                setLoader((prev) => ({ ...prev, mostrar: false }));
            })
            .catch(() => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire(
                    "FAMILIA DE REFACCIONES",
                    "No fue posible cargar el listado. Intente nuevamente o contacte al administrador.",
                    "error"
                );
            });
    };

    // Carga inicial única; el resto de recargas viene de onGuardado del modal.
    useEffect(() => {
        cargarListado();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onAgregar = () => {
        setRegistroEditar(null);
        setOpenModal(true);
    };

    const onEditar = (registro) => {
        setRegistroEditar(registro);
        setOpenModal(true);
    };

    const onCerrarModal = () => {
        setOpenModal(false);
        setRegistroEditar(null);
    };

    // Búsqueda en memoria por parte/familia/sub familia (el endpoint no acepta parámetro de búsqueda).
    const listadoFiltrado = listado.filter((fila) => {
        if (!busqueda.trim()) return true;
        const texto = busqueda.trim().toLowerCase();
        return (
            String(fila.numero_parte || "").toLowerCase().includes(texto) ||
            String(fila.nombre_parte || "").toLowerCase().includes(texto) ||
            String(fila.familia || "").toLowerCase().includes(texto) ||
            String(fila.subfamilia_1 || "").toLowerCase().includes(texto) ||
            String(fila.subfamilia_2 || "").toLowerCase().includes(texto)
        );
    });

    // Vuelve a la página 1 cuando cambia la búsqueda o el tamaño de página,
    // para no quedar en una página vacía si el nuevo resultado tiene menos.
    useEffect(() => {
        setPaginaActual(1);
    }, [busqueda, registrosPorPagina]);

    const totalRegistros = listadoFiltrado.length;
    const totalPaginas = Math.max(1, Math.ceil(totalRegistros / registrosPorPagina));
    const paginaSegura = Math.min(paginaActual, totalPaginas);
    const inicioPagina = (paginaSegura - 1) * registrosPorPagina;
    const finPagina = Math.min(inicioPagina + registrosPorPagina, totalRegistros);
    const listadoPaginado = listadoFiltrado.slice(inicioPagina, finPagina);

    const irPrimeraPagina = () => setPaginaActual(1);
    const irPaginaAnterior = () => setPaginaActual((p) => Math.max(1, p - 1));
    const irPaginaSiguiente = () => setPaginaActual((p) => Math.min(totalPaginas, p + 1));
    const irUltimaPagina = () => setPaginaActual(totalPaginas);

    return (
        <PlantillaScreen
            titulo={"FAMILIA DE REFACCIONES"}
            className={"familia-refacciones-main-screen"}
            className_main={1}
        >
            <LoaderScreen loader={loader} />
            <div className="frf-actions">
                <div className="frf-actions-regresar" onClick={() => history.goBack()}>
                    <ArrowBackIcon /> Regresar
                </div>
            </div>

            <div className="frf-container">
                <div className="frf-header">
                    <TextField
                        className="frf-buscador"
                        size="small"
                        label="Buscar"
                        placeholder="Parte, familia o sub familia..."
                        value={busqueda}
                        onChange={({ target }) => setBusqueda(target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <button className="frf-agregar" onClick={onAgregar} type="button">
                        <AddCircleOutlineIcon /> Agregar
                    </button>
                </div>

                <div className="frf-tabla scroll">
                    <table cellPadding={0} cellSpacing={0}>
                        <thead>
                            <tr>
                                <th># Parte</th>
                                <th>Parte</th>
                                <th>Familia</th>
                                <th>Sub Familia 1</th>
                                <th>Sub Familia 2</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {listadoPaginado.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="frf-vacio">
                                        Sin registros para mostrar.
                                    </td>
                                </tr>
                            ) : (
                                listadoPaginado.map((fila) => (
                                    <tr key={fila.idlistado}>
                                        <td>{fila.numero_parte}</td>
                                        <td>{fila.nombre_parte}</td>
                                        <td>{fila.familia}</td>
                                        <td>{fila.subfamilia_1 || "—"}</td>
                                        <td>{fila.subfamilia_2 || "—"}</td>
                                        <td className="frf-columna-editar">
                                            <div className="frf-boton-editar" onClick={() => onEditar(fila)}>
                                                <EditIcon /> Editar
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="frf-paginador">
                    <div className="frf-paginador-info">
                        Mostrando {totalRegistros === 0 ? 0 : inicioPagina + 1}
                        {" - "}
                        {finPagina}
                        {" de "}
                        {totalRegistros}
                    </div>

                    <div className="frf-paginador-controles">
                        <select
                            className="frf-paginador-select"
                            value={registrosPorPagina}
                            onChange={({ target }) => setRegistrosPorPagina(Number(target.value))}
                        >
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                        </select>

                        <button
                            className="frf-paginador-btn"
                            onClick={irPrimeraPagina}
                            disabled={paginaSegura === 1}
                            title="Primera página"
                            type="button"
                        >
                            <FirstPageIcon fontSize="small" />
                        </button>
                        <button
                            className="frf-paginador-btn"
                            onClick={irPaginaAnterior}
                            disabled={paginaSegura === 1}
                            title="Página anterior"
                            type="button"
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </button>

                        <span className="frf-paginador-pagina">
                            Página {paginaSegura} de {totalPaginas}
                        </span>

                        <button
                            className="frf-paginador-btn"
                            onClick={irPaginaSiguiente}
                            disabled={paginaSegura === totalPaginas}
                            title="Página siguiente"
                            type="button"
                        >
                            <ChevronRightIcon fontSize="small" />
                        </button>
                        <button
                            className="frf-paginador-btn"
                            onClick={irUltimaPagina}
                            disabled={paginaSegura === totalPaginas}
                            title="Última página"
                            type="button"
                        >
                            <LastPageIcon fontSize="small" />
                        </button>
                    </div>
                </div>
            </div>

            <FamiliaRefaccionesModal
                open={openModal}
                registro={registroEditar}
                onClose={onCerrarModal}
                onGuardado={cargarListado}
            />
        </PlantillaScreen>
    );
};

export default FamiliaRefaccionesMainScreen;
